/**
 * Navigation API Client
 *
 * Split from platform.ts – navigation registry endpoints.
 */

import client from './client';

const api = {
  get: <T = unknown>(path: string, config?: Record<string, unknown>) => client.get<T>(`/api/platform${path}`, config),
  post: <T = unknown>(path: string, data?: unknown, config?: Record<string, unknown>) => client.post<T>(`/api/platform${path}`, data, config),
  put: <T = unknown>(path: string, data?: unknown, config?: Record<string, unknown>) => client.put<T>(`/api/platform${path}`, data, config),
  delete: <T = unknown>(path: string, config?: Record<string, unknown>) => client.delete<T>(`/api/platform${path}`, config),
};

// ============================================================
// NAVIGATION INTERFACES
// ============================================================

export interface NavigationItem {
  id: string;
  pluginId: string;
  label: string;
  path: string;
  icon?: string;
  order?: number;
  group?: 'main' | 'secondary' | 'footer' | 'user';
  /**
   * Navigation category this item belongs to (e.g. 'Content', 'Knowledge').
   * If set, the item is placed into the named category dropdown.
   * Wave A9.14: The canonical source is plugin.manifest.json `navigation[].category`.
   */
  category?: string;
  /**
   * Where this nav item appears (Wave A9.14).
   * - 'top'     — visible in the top nav bar (default)
   * - 'sidebar' — visible only in sub-nav within a parent page
   * - 'hidden'  — registered route but not shown in any nav
   * - 'devtools' — only visible in DevTools / admin navigation
   */
  navVisibility?: 'top' | 'sidebar' | 'hidden' | 'devtools';
  requiredPermissions?: string[];
  parentId?: string;
  isExternal?: boolean;
  badge?: string | number;
  badgeVariant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  tooltip?: string;
  children?: NavigationItem[];
  registeredAt: string;
}

export interface NavigationCategory {
  id: string;
  pluginId: string;
  title: string;
  icon?: string;
  order: number;
  items: NavigationItem[];
}

export interface NavigationData {
  items: NavigationItem[];
  categories: NavigationCategory[];
}

// ============================================================
// NAVIGATION FUNCTIONS
// ============================================================

export async function getNavigation(): Promise<NavigationData> {
  const response = await api.get<NavigationData>('/navigation');
  return response.data;
}

export async function getNavigationItems(group?: string, parentId?: string): Promise<NavigationItem[]> {
  const params = new URLSearchParams();
  if (group) params.append('group', group);
  if (parentId) params.append('parentId', parentId);
  const response = await api.get<NavigationItem[]>(`/navigation/items?${params}`);
  return response.data;
}

export async function getNavigationCategories(): Promise<NavigationCategory[]> {
  const response = await api.get<NavigationCategory[]>('/navigation/categories');
  return response.data;
}
